import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { addTextTestimonial, clearTextTestimonialStatus } from 'slice/textTestimonialSlice';

const ensureTrailingSlash = (value = '') => (value.endsWith('/') ? value : `${value}/`);

const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);

const getProfileImageUrl = (value = '') => {
  if (!value) {
    return '';
  }

  if (isAbsoluteUrl(value)) {
    return value;
  }

  const normalized = String(value).replace(/\\/g, '/').replace(/^\/+/, '');
  const uploadsBase = process.env.REACT_APP_BACKEND_URL_REL
    ? ensureTrailingSlash(process.env.REACT_APP_BACKEND_URL_REL)
    : '';

  if (uploadsBase && normalized.startsWith('text_testimonials/')) {
    return `${uploadsBase}${normalized}`;
  }

  const backendBase = process.env.REACT_APP_BACKEND ? ensureTrailingSlash(process.env.REACT_APP_BACKEND) : '';
  return backendBase ? `${backendBase}${normalized}` : normalized;
};

const Add = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, lastAction } = useSelector((state) => state.textTestimonial);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    review_text: '',
    rating: 5,
    review_time: '1 year ago',
    is_active: 1,
    profile_image_url: '',
    profile_image_file: null,
  });

  useEffect(() => {
    dispatch(clearTextTestimonialStatus());
    setHasSubmitted(false);
  }, [dispatch]);

  useEffect(() => {
    if (!formData.profile_image_file) {
      setProfileImagePreview('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.profile_image_file);
    setProfileImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.profile_image_file]);

  useEffect(() => {
    if (hasSubmitted && lastAction === 'add' && !loading && !error) {
      toast('Text testimonial added successfully!');
      dispatch(clearTextTestimonialStatus());
      setHasSubmitted(false);
      navigate('/text-testimonial');
    }
  }, [dispatch, error, hasSubmitted, lastAction, loading, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'rating' || name === 'is_active' ? Number(value) : value,
    }));
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      profile_image_file: file,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.full_name.trim()) {
      toast('Full name is required.');
      return;
    }

    if (!formData.review_text.trim()) {
      toast('Review text is required.');
      return;
    }

    const payload = new FormData();
    payload.append('full_name', formData.full_name.trim());
    payload.append('review_text', formData.review_text.trim());
    payload.append('rating', String(formData.rating));
    payload.append('review_time', formData.review_time.trim() || '1 year ago');
    payload.append('is_active', String(formData.is_active));

    if (formData.profile_image_file) {
      payload.append('profile_image', formData.profile_image_file);
    } else if (formData.profile_image_url.trim()) {
      payload.append('profile_image', formData.profile_image_url.trim());
    }

    setHasSubmitted(true);
    dispatch(addTextTestimonial(payload));
  };

  const currentProfileImageUrl = profileImagePreview || getProfileImageUrl(formData.profile_image_url.trim());

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="text-white font-weight-bold">Add Text Testimonial</h4>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row mx-4">
          <div className="col-sm-6 mt-3">
            <label htmlFor="testimonialFullName" className="adminlables">
              Full Name
            </label>
            <input
              id="testimonialFullName"
              name="full_name"
              type="text"
              className="form-control"
              value={formData.full_name}
              onChange={handleChange}
            />
          </div>

          <div className="col-sm-3 mt-3">
            <label htmlFor="testimonialRating" className="adminlables">
              Rating
            </label>
            <select
              id="testimonialRating"
              name="rating"
              className="form-control"
              value={formData.rating}
              onChange={handleChange}
            >
              {[5, 4, 3, 2, 1].map((rating) => (
                <option key={rating} value={rating}>
                  {rating} / 5
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-3 mt-3">
            <label htmlFor="testimonialStatus" className="adminlables">
              Status
            </label>
            <select
              id="testimonialStatus"
              name="is_active"
              className="form-control"
              value={formData.is_active}
              onChange={handleChange}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="testimonialReviewText" className="adminlables">
              Review Text
            </label>
            <textarea
              id="testimonialReviewText"
              name="review_text"
              rows={5}
              className="form-control"
              value={formData.review_text}
              onChange={handleChange}
            />
          </div>

          <div className="col-sm-6 mt-4">
            <label htmlFor="testimonialReviewTime" className="adminlables">
              Review Time
            </label>
            <input
              id="testimonialReviewTime"
              name="review_time"
              type="text"
              className="form-control"
              value={formData.review_time}
              onChange={handleChange}
            />
          </div>

          <div className="col-sm-6 mt-4">
            <label htmlFor="testimonialProfileImageFile" className="adminlables">
              Profile Image
            </label>
            <input
              id="testimonialProfileImageFile"
              type="file"
              name="profile_image_file"
              className="form-control btn-block filebg"
              accept="image/*"
              onChange={handleProfileImageChange}
            />
          </div>

          <div className="col-sm-12 mt-4">
            <label htmlFor="testimonialProfileImageUrl" className="adminlables">
              Profile Image URL
            </label>
            <input
              id="testimonialProfileImageUrl"
              name="profile_image_url"
              type="url"
              className="form-control"
              value={formData.profile_image_url}
              onChange={handleChange}
            />
          </div>

          {currentProfileImageUrl && (
            <div className="col-sm-4 mt-4">
              <div className="adminlables">Current Image</div>
              <div>
                <img
                  src={currentProfileImageUrl}
                  alt={formData.full_name || 'testimonial'}
                  className="img-thumbnail"
                  style={{ maxWidth: 220, width: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 text-right mx-4">
          <button type="submit" className="btn addbtn btn-lg" disabled={loading}>
            Add Text Testimonial
          </button>
        </div>
      </form>
    </div>
  );
};

export default Add;
